import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useScrollToElement } from "@/hooks/useScrollToElement";
import { LessonStatus } from "@/components/course/tree/types";
import {
  buildIntroUnveilQueryString,
  buildOutroContentEntryQueryString,
  resolveFirstSubIntroChapterId,
  resolveLastSubOutroChapterId,
  stripCourseNavQueryString,
} from "@/lib/chapterNavigation";
import { useCourseBasePath } from "@/hooks/useCourseBasePath";
import {
  sectionKey,
  useSectionCompletionStore,
} from "@/stores/sectionCompletionStore";

export const useSyncSelectedCourseNode = () => {
  const { nodeId: selectedNodeId, contentElementId } = useParams<{
    nodeId: string;
    contentElementId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const basePath = useCourseBasePath();
  const {
    selectNode,
    setViewMode,
    expandChapters,
    expandModule,
    resetExpandedChapters,
    nodeIndex,
    progressSubchapters,
  } = useCourseDetailStore();
  const completedSections = useSectionCompletionStore(
    (state) => state.completed
  );

  useScrollToElement(selectedNodeId ? `node-${selectedNodeId}` : null);

  useEffect(() => {
    if (!selectedNodeId || nodeIndex.size === 0) return;

    // Deep links to a specific content element must keep lesson + element ids.
    // Rewriting to the stacked chapter unveil/outro route would drop the
    // element segment (e.g. trainer "An der Position des Lernenden einsteigen").
    const hasContentElementDeepLink = Boolean(contentElementId);

    const introChapterId = resolveFirstSubIntroChapterId(
      selectedNodeId,
      nodeIndex
    );
    // A bare first-lesson link opens the chapter introduction. Once that lesson
    // has been started, or the chapter introduction is already checked, open
    // the lesson content instead.
    if (introChapterId && !hasContentElementDeepLink) {
      const lessonStatus = progressSubchapters[selectedNodeId]?.status;
      const lessonAlreadyStarted =
        lessonStatus === LessonStatus.InProgress ||
        lessonStatus === LessonStatus.Completed;
      const chapterIntroChecked = completedSections.has(
        sectionKey("intro", introChapterId)
      );
      const introSearch =
        lessonAlreadyStarted || chapterIntroChecked
          ? buildIntroUnveilQueryString(searchParams)
          : stripCourseNavQueryString(searchParams);
      navigate(`${basePath}/${introChapterId}${introSearch}`, {
        replace: true,
      });
      return;
    }

    const outroChapterId = resolveLastSubOutroChapterId(
      selectedNodeId,
      nodeIndex
    );
    if (outroChapterId && !hasContentElementDeepLink) {
      navigate(
        `${basePath}/${outroChapterId}${buildOutroContentEntryQueryString(searchParams)}`,
        { replace: true }
      );
      return;
    }

    selectNode(selectedNodeId);

    // Override viewMode if ?view=outro is present
    const viewParam = searchParams.get("view");
    if (viewParam === "outro") {
      setViewMode("outro");
    }

    const entry = nodeIndex.get(selectedNodeId);
    if (!entry) return;

    // reset default expanded chapters
    resetExpandedChapters();

    const chaptersToExpand: string[] = [];

    let currentEntry = entry;
    while (currentEntry.parent) {
      const parent = currentEntry.parent;

      if (parent.type === "chapter") {
        chaptersToExpand.push(parent.id);
      }

      if (parent.type === "module") {
        expandModule(parent.id);
      }

      const nextEntry = nodeIndex.get(parent.id);
      if (!nextEntry) break;

      currentEntry = nextEntry;
    }

    if (entry.node.type === "chapter") {
      chaptersToExpand.push(entry.node.id);
    }

    // Also expand the parent module from the nodeIndex entry
    if (entry.module) {
      expandModule(entry.module.id);
    }

    if (chaptersToExpand.length > 0) {
      expandChapters(chaptersToExpand);
    }
  }, [
    selectedNodeId,
    contentElementId,
    searchParams,
    nodeIndex,
    selectNode,
    setViewMode,
    expandChapters,
    expandModule,
    resetExpandedChapters,
    navigate,
    basePath,
    progressSubchapters,
    completedSections,
  ]);
};
